import type { ApiSchemas } from '@rental/contracts/client'
import { format, localized, plural } from '@rental/i18n'
import { cn } from '@rental/ui'
import { Ban } from 'lucide-react'
import { type KeyboardEvent as ReactKeyboardEvent, useId, useMemo, useState } from 'react'
import { addDays } from '../board'
import { Section } from '../components/Section'
import { useLocale } from '../i18n'
import { DAYS_GRID, dayLabel, scrollTogether } from '../screens/Timeline'
import {
  cellName,
  type DayValues,
  freeWords,
  NOTHING_SET,
  type Occupancy,
  priceShown,
} from './cell'
import type { AskedDay } from './DayDialog'
import { type CellPosition, nextCell } from './grid'

type Calendar = ApiSchemas['StaffPricesCalendar']
type Category = Calendar['categories'][number]

/** The categories' names, 128 px, which the days scroll under (as the Planning's plates). */
const LABEL = 'w-32 shrink-0'
/** A day's least width: its words wrap inside it, so every category's days stay one under another. */
const DAY = 'min-w-32'

const at = (row: number, col: number) => `${row}:${col}`

/**
 * The rate calendar's board (W2-S S1). One stop for the Tab key: the arrows
 * move between the days that can be changed (a day past is shown, not a
 * button), Home and End to a row's first and last, and Enter or Space opens
 * the day, as a button does. The focus a press of the arrows lands on is the
 * next Tab stop, so coming back finds the same day.
 */
export function PricesBoard({
  calendar,
  fetching,
  onOpen,
}: {
  calendar: Calendar
  fetching: boolean
  onOpen: (day: AskedDay) => void
}) {
  const dates = useMemo(
    () => Array.from({ length: calendar.days }, (_, day) => addDays(calendar.from, day)),
    [calendar.from, calendar.days],
  )
  const firstCol = (() => {
    const index = dates.findIndex((date) => date >= calendar.today)
    return index === -1 ? dates.length : index
  })()
  const cells = useMemo(
    () => new Map(calendar.cells.map((cell) => [`${cell.categoryId}:${cell.day}`, cell])),
    [calendar.cells],
  )
  const occupancy = useMemo(
    () =>
      new Map(
        calendar.occupancy.map((entry) => [
          `${entry.categoryId}:${entry.day}`,
          { free: entry.free, total: entry.total },
        ]),
      ),
    [calendar.occupancy],
  )
  const [active, setActive] = useState<CellPosition | null>(null)
  const rows = calendar.categories.length
  const valid = (position: CellPosition | null): position is CellPosition =>
    position !== null &&
    position.row < rows &&
    position.col >= firstCol &&
    position.col < dates.length
  const stop: CellPosition | null = valid(active)
    ? active
    : firstCol < dates.length
      ? { row: 0, col: firstCol }
      : null

  /** An arrow, Home or End on a day: the focus moves, and the next Tab stop with it. */
  const onCellKey = (event: ReactKeyboardEvent<HTMLButtonElement>, from: CellPosition) => {
    const next = nextCell(from, event.key, { rows, cols: dates.length, firstCol })
    if (!next) return
    event.preventDefault()
    setActive(next)
    event.currentTarget
      .closest('[data-prices-board]')
      ?.querySelector<HTMLButtonElement>(`[data-cell="${at(next.row, next.col)}"]`)
      ?.focus()
  }

  return (
    <div
      ref={scrollTogether}
      className="flex flex-col gap-6"
      aria-busy={fetching || undefined}
      data-prices-board=""
    >
      {calendar.categories.map((category, row) => (
        <CategoryRow
          key={category.id}
          category={category}
          row={row}
          dates={dates}
          today={calendar.today}
          boundarySet={calendar.rentalDayBoundary !== null}
          stop={stop}
          valuesOf={(day) => cells.get(`${category.id}:${day}`) ?? null}
          occupancyOf={(day) =>
            occupancy.get(`${category.id}:${day}`) ?? { free: null, total: category.activeUnits }
          }
          onFocusCell={setActive}
          onCellKey={onCellKey}
          onOpen={onOpen}
        />
      ))}
    </div>
  )
}

function CategoryRow({
  category,
  row,
  dates,
  today,
  boundarySet,
  stop,
  valuesOf,
  occupancyOf,
  onFocusCell,
  onCellKey,
  onOpen,
}: {
  category: Category
  row: number
  dates: readonly string[]
  today: string
  boundarySet: boolean
  stop: CellPosition | null
  valuesOf: (day: string) => ApiSchemas['StaffPriceCell'] | null
  occupancyOf: (day: string) => Occupancy
  onFocusCell: (position: CellPosition) => void
  onCellKey: (event: ReactKeyboardEvent<HTMLButtonElement>, at: CellPosition) => void
  onOpen: (day: AskedDay) => void
}) {
  const { locale, messages: m } = useLocale()
  const words = m.prices
  const headingId = useId()
  const name = localized(category.name, locale)
  return (
    <Section
      title={name}
      headingId={headingId}
      status={
        <span className="text-sm text-text-muted">
          {plural(locale, words.activeUnits, category.activeUnits)}
        </span>
      }
    >
      {/*
        The category's days, named by its heading: when they do not fit they scroll sideways,
        under its name, which stays. It takes the focus, so a keyboard can scroll it; a day the
        focus reaches is scrolled clear of the name (scroll-ps-32). Positioned, as ScrollRegion
        is: a past day's hidden name stays inside what scrolls.
      */}
      <section
        aria-labelledby={headingId}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: a region that scrolls takes focus to be scrolled without a mouse
        tabIndex={0}
        data-days=""
        className="relative isolate overflow-x-auto scroll-ps-32"
      >
        <div className="w-max min-w-full">
          <div className="flex border-b border-rule">
            <div
              className={cn(
                LABEL,
                'sticky start-0 z-10 border-e border-rule bg-surface-pane-strong',
              )}
            />
            <div className={cn(DAYS_GRID, 'grow')}>
              {dates.map((date, day) => (
                <span
                  key={date}
                  data-day={date}
                  className={cn(
                    'row-start-1 truncate border-s border-rule px-2 py-1 text-xs',
                    DAY,
                    `col-start-${day + 1}`,
                    date === today ? 'bg-surface-well font-bold text-brand' : 'text-text-muted',
                  )}
                >
                  {dayLabel(locale, date, day === 0)}
                </span>
              ))}
            </div>
          </div>
          <div className="flex">
            <div
              className={cn(
                LABEL,
                'sticky start-0 z-10 flex items-center border-e border-rule bg-surface-pane-strong pe-2 font-bold wrap-anywhere',
              )}
            >
              {name}
            </div>
            <div className={cn(DAYS_GRID, 'grow')}>
              {dates.map((date, col) => {
                const cell = valuesOf(date)
                const values: DayValues = cell ?? NOTHING_SET
                const occupancy = occupancyOf(date)
                const past = date < today
                const label = cellName({
                  locale,
                  words,
                  category: name,
                  day: date,
                  values,
                  occupancy,
                  boundarySet,
                  past,
                })
                const place = cn('row-start-1 border-s border-rule', DAY, `col-start-${col + 1}`)
                const content = (
                  <CellContent
                    values={values}
                    occupancy={occupancy}
                    boundarySet={boundarySet}
                    past={past}
                  />
                )
                if (past) {
                  return (
                    <div key={date} className={cn(place, 'bg-surface-well px-2 py-2')}>
                      <span className="sr-only">{label}</span>
                      <div aria-hidden="true">{content}</div>
                    </div>
                  )
                }
                const isStop = stop?.row === row && stop.col === col
                return (
                  <button
                    key={date}
                    type="button"
                    data-cell={at(row, col)}
                    tabIndex={isStop ? 0 : -1}
                    aria-label={label}
                    className={cn(
                      place,
                      'flex min-h-touch cursor-pointer flex-col items-stretch px-2 py-2 text-start transition-colors hover:bg-surface-well',
                    )}
                    onFocus={() => onFocusCell({ row, col })}
                    onKeyDown={(event) => onCellKey(event, { row, col })}
                    onClick={() =>
                      onOpen({
                        categoryId: category.id,
                        categoryName: name,
                        day: date,
                        values,
                        version: cell?.version ?? null,
                      })
                    }
                  >
                    {content}
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      </section>
    </Section>
  )
}

/**
 * What a day shows: its price (a dash for none), its minimums when set, stop
 * sale as a mark and in words, and the free vehicles of the total, or why
 * they are not known. Never how many are booked.
 */
function CellContent({
  values,
  occupancy,
  boundarySet,
  past,
}: {
  values: DayValues
  occupancy: Occupancy
  boundarySet: boolean
  past: boolean
}) {
  const { locale, messages: m } = useLocale()
  const words = m.prices
  const free = freeWords(words, occupancy, boundarySet)
  return (
    <span className="flex flex-col gap-1 text-xs wrap-anywhere">
      <span
        className={cn(
          'figures text-base font-bold',
          past ? 'text-text-muted' : 'text-brand-strong',
        )}
      >
        {priceShown(locale, words, values.priceCents)}
      </span>
      {values.minDaysPickup !== null ? (
        <span>{format(words.minPickup, { count: values.minDaysPickup })}</span>
      ) : null}
      {values.minDaysThrough !== null ? (
        <span>{format(words.minThrough, { count: values.minDaysThrough })}</span>
      ) : null}
      {values.stopSale ? (
        <span className="flex items-center gap-1 font-bold text-danger">
          <Ban aria-hidden="true" className="size-3 shrink-0" />
          {words.stopSale}
        </span>
      ) : null}
      <span className="figures text-text-muted">
        {free.shown}
        {free.note ? ` · ${free.note}` : null}
      </span>
    </span>
  )
}
